import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND_DETAIL } from "@/constants/testIds";
import StatementsPanel from "@/components/StatementsPanel";

const ROLES = [
  { value: "brand_manager", label: "Brand Manager" },
  { value: "location_manager", label: "Location Manager" },
  { value: "local_marketer", label: "Local Marketer" },
  { value: "approver", label: "Approver" },
  { value: "fulfillment_partner", label: "Fulfillment Partner" },
];

export default function BrandDetail() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/brands/${brandId}`);
      setBrand(data);
    } catch {
      setError("Brand not found");
      return;
    }
    const [{ data: m }, { data: l }] = await Promise.all([
      api.get(`/brands/${brandId}/members`),
      api.get(`/brands/${brandId}/locations`),
    ]);
    setMembers(m);
    setLocations(l);
  };

  useEffect(() => {
    load();
  }, [brandId]);

  if (error) return <p className="text-[#B5462F]">{error}</p>;
  if (!brand) return <p className="text-[#6B6358]">Loading&hellip;</p>;

  return (
    <div data-testid={BRAND_DETAIL.root}>
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">Brand</p>
      <h1 className="font-serif text-5xl tracking-tight">{brand.name}</h1>
      {brand.description && (
        <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">{brand.description}</p>
      )}

      <div className="mt-10 flex gap-1 border-b border-[#1A1F2C]/10 max-w-4xl">
        <TabButton
          testid={BRAND_DETAIL.membersTab}
          active={tab === "members"}
          onClick={() => setTab("members")}
        >
          Members &middot; {members.length}
        </TabButton>
        <TabButton
          testid={BRAND_DETAIL.locationsTab}
          active={tab === "locations"}
          onClick={() => setTab("locations")}
        >
          Locations &middot; {locations.length}
        </TabButton>
        <TabButton
          testid="brand-detail-settings-tab"
          active={tab === "settings"}
          onClick={() => setTab("settings")}
        >
          Settings
        </TabButton>
        <button
          data-testid="brand-detail-brand-kit-tab"
          onClick={() => navigate(`/brands/${brandId}/brand-kit`)}
          className="ml-auto text-sm px-5 py-3 text-[#B5462F] hover:text-[#1A1F2C]"
        >
          Brand Kit &rarr;
        </button>
        <button
          data-testid="brand-detail-attributes-tab"
          onClick={() => navigate(`/brands/${brandId}/attributes`)}
          className="text-sm px-5 py-3 text-[#B5462F] hover:text-[#1A1F2C]"
        >
          Location Details &rarr;
        </button>
      </div>

      <div className="mt-8 max-w-4xl">
        {tab === "members" && (
          <MembersPanel
            brandId={brandId}
            members={members}
            locations={locations}
            canManage={!!user?.is_platform_admin}
            onChanged={load}
          />
        )}
        {tab === "locations" && (
          <LocationsPanel brandId={brandId} locations={locations} onChanged={load} />
        )}
        {tab === "settings" && (
          <div className="space-y-12">
            <StatementsPanel
              brandId={brandId}
              canManage={!!user?.is_platform_admin}
              title="Brand Voice"
              description="Up to 10 short declarations of how the brand sounds and feels. These get injected into the AI Assembly Agent's prompt so every generated word carries the brand's voice. Keep each line under ~140 characters, and use positive language (“Our work feels …”) rather than negative (“Don't …”) — the rules library already handles the don'ts."
              fieldKey="dna_statements"
              saveKey="dna_statements"
              endpoint={`/brands/${brandId}/dna`}
              testidPrefix="brand-dna"
              placeholder='e.g. "We feel confident, uncrowded, and warm."'
              saveLabel="Save Brand Voice"
            />
            <StatementsPanel
              brandId={brandId}
              canManage={!!user?.is_platform_admin}
              title="Visual Identity"
              description="Up to 10 short declarations of how the brand looks — color, type, imagery, and composition. Same length as Brand Voice (keep each line under ~140 characters). Use positive direction (“Generous white space, never crowded”) to guide the design half of the AI Assembly Agent."
              fieldKey="visual_identity_statements"
              saveKey="visual_identity_statements"
              endpoint={`/brands/${brandId}/visual-identity`}
              testidPrefix="brand-visual-identity"
              placeholder='e.g. "Warm neutrals, a single rust accent, generous margins."'
              saveLabel="Save Visual Identity"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-[#B5462F] text-[#1A1F2C]"
          : "border-transparent text-[#6B6358] hover:text-[#1A1F2C]"
      }`}
    >
      {children}
    </button>
  );
}

function MembersPanel({ brandId, members, locations, canManage, onChanged }) {
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("brand_manager");
  const [locIds, setLocIds] = useState([]);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/brands/${brandId}/members`, {
        user_email: email,
        user_name: name,
        role,
        location_ids: locIds,
      });
      setEmail("");
      setName("");
      setLocIds([]);
      setInviting(false);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not invite member");
    }
  };

  const toggleLoc = (id) =>
    setLocIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <>
      <div className="flex justify-end mb-4">
        {canManage && !inviting && (
          <button
            data-testid={BRAND_DETAIL.inviteButton}
            onClick={() => setInviting(true)}
            className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm"
          >
            Invite member
          </button>
        )}
      </div>

      {inviting && (
        <form
          onSubmit={submit}
          className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6"
        >
          <h3 className="font-serif text-lg mb-4">Invite a member</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
                Email
              </label>
              <input
                data-testid={BRAND_DETAIL.inviteEmailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
                Name
              </label>
              <input
                data-testid={BRAND_DETAIL.inviteNameInput}
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
                Role
              </label>
              <select
                data-testid={BRAND_DETAIL.inviteRoleSelect}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
                Assigned locations
              </label>
              <div className="flex flex-wrap gap-1.5">
                {locations.length === 0 && (
                  <span className="text-xs text-[#6B6358]">Add a location first (Locations tab).</span>
                )}
                {locations.map((l) => {
                  const on = locIds.includes(l.location_id);
                  return (
                    <button
                      key={l.location_id}
                      type="button"
                      data-testid={`invite-location-${l.location_id}`}
                      onClick={() => toggleLoc(l.location_id)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                        on ? "bg-[#1F6B6B] text-white border-[#1F6B6B]" : "bg-[#F8F5EE] text-[#3D3A35] border-[#1A1F2C]/20"
                      }`}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button
              type="submit"
              data-testid={BRAND_DETAIL.inviteSubmit}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
            >
              Add member
            </button>
            <button
              type="button"
              data-testid={BRAND_DETAIL.inviteCancel}
              onClick={() => setInviting(false)}
              className="px-5 py-2 text-sm text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
        {members.length === 0 && (
          <div className="px-6 py-6 text-sm text-[#6B6358]">No members yet.</div>
        )}
        {members.map((m) => (
          <div
            key={m.membership_id}
            data-testid={`${BRAND_DETAIL.memberRowPrefix}${m.user_id}`}
            className="px-6 py-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {m.picture ? (
                  <img src={m.picture} alt={m.name} className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#B5462F] text-[#F8F5EE] flex items-center justify-center text-sm">
                    {m.name?.[0] || "?"}
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium">{m.name}</div>
                  <div className="text-xs text-[#6B6358]">{m.email}</div>
                </div>
              </div>
              <span className="text-xs uppercase tracking-[0.15em] text-[#6B6358]">
                {ROLES.find((r) => r.value === m.role)?.label || m.role}
              </span>
            </div>
            <MemberLocations
              brandId={brandId}
              member={m}
              locations={locations}
              canManage={canManage}
              onChanged={onChanged}
            />
          </div>
        ))}
      </div>
    </>
  );
}

function MemberLocations({ brandId, member, locations, canManage, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(member.location_ids || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = (id) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(`/brands/${brandId}/members/${member.user_id}/locations`, {
        location_ids: sel,
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not update locations");
    } finally {
      setSaving(false);
    }
  };

  const assigned = member.locations || [];

  return (
    <div className="mt-3 pl-12" data-testid={`member-locations-${member.user_id}`}>
      {!editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6358]">Locations:</span>
          {assigned.length === 0 ? (
            <span
              data-testid={`member-locations-${member.user_id}-none`}
              className="text-xs text-[#B5462F]"
            >
              None assigned — can&rsquo;t produce yet
            </span>
          ) : (
            assigned.map((l) => (
              <span
                key={l.location_id}
                className="text-xs bg-[#1F6B6B]/10 text-[#1F6B6B] rounded-full px-2.5 py-0.5"
              >
                {l.name}
              </span>
            ))
          )}
          {canManage && (
            <button
              data-testid={`member-locations-${member.user_id}-edit`}
              onClick={() => { setSel(member.location_ids || []); setEditing(true); }}
              className="text-[10px] uppercase tracking-[0.14em] text-[#1A1F2C] border border-[#1A1F2C]/20 rounded-full px-2.5 py-1 hover:bg-[#1A1F2C]/[0.04]"
            >
              Edit
            </button>
          )}
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-1.5">
            {locations.length === 0 && (
              <span className="text-xs text-[#6B6358]">Add a location first (Locations tab).</span>
            )}
            {locations.map((l) => {
              const on = sel.includes(l.location_id);
              return (
                <button
                  key={l.location_id}
                  type="button"
                  data-testid={`member-locations-${member.user_id}-opt-${l.location_id}`}
                  onClick={() => toggle(l.location_id)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    on ? "bg-[#1F6B6B] text-white border-[#1F6B6B]" : "bg-[#F8F5EE] text-[#3D3A35] border-[#1A1F2C]/20"
                  }`}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
          {error && <p className="text-xs text-[#B5462F] mt-2">{error}</p>}
          <div className="flex gap-2 mt-2">
            <button
              data-testid={`member-locations-${member.user_id}-save`}
              onClick={save}
              disabled={saving}
              className="px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] bg-[#1A1F2C] text-[#F8F5EE] rounded-full disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationsPanel({ brandId, locations, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address_line1: "",
    city: "",
    state: "",
    postal_code: "",
    phone: "",
  });
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/brands/${brandId}/locations`, form);
      setForm({ name: "", address_line1: "", city: "", state: "", postal_code: "", phone: "" });
      setAdding(false);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not add location");
    }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        {!adding && (
          <button
            data-testid={BRAND_DETAIL.addLocationButton}
            onClick={() => setAdding(true)}
            className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm"
          >
            Add location
          </button>
        )}
      </div>

      {adding && (
        <form
          onSubmit={submit}
          className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6"
        >
          <h3 className="font-serif text-lg mb-4">Add a location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LField label="Name" testid={BRAND_DETAIL.locationNameInput} value={form.name} onChange={set("name")} required />
            <LField label="Phone" testid={BRAND_DETAIL.locationPhoneInput} value={form.phone} onChange={set("phone")} />
            <LField label="Address" testid={BRAND_DETAIL.locationAddressInput} value={form.address_line1} onChange={set("address_line1")} className="md:col-span-2" />
            <LField label="City" testid={BRAND_DETAIL.locationCityInput} value={form.city} onChange={set("city")} />
            <LField label="State" testid={BRAND_DETAIL.locationStateInput} value={form.state} onChange={set("state")} />
            <LField label="Postal Code" testid={BRAND_DETAIL.locationPostalInput} value={form.postal_code} onChange={set("postal_code")} />
          </div>
          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <div className="flex gap-3 mt-5">
            <button
              type="submit"
              data-testid={BRAND_DETAIL.locationSubmit}
              className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
            >
              Add location
            </button>
            <button
              type="button"
              data-testid={BRAND_DETAIL.locationCancel}
              onClick={() => setAdding(false)}
              className="px-5 py-2 text-sm text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
        {locations.length === 0 && (
          <div className="px-6 py-6 text-sm text-[#6B6358]">No locations yet.</div>
        )}
        {locations.map((l) => (
          <Link
            key={l.location_id}
            to={`/brands/${brandId}/locations/${l.location_id}`}
            data-testid={`${BRAND_DETAIL.locationRowPrefix}${l.location_id}`}
            className="block px-6 py-4 hover:bg-[#F8F5EE]"
          >
            <div className="flex items-baseline justify-between">
              <div className="font-serif text-lg">{l.name}</div>
              {l.location_code && (
                <span className="font-mono text-xs text-[#6B6358]">{l.location_code}</span>
              )}
            </div>
            <div className="text-sm text-[#6B6358] mt-1">
              {[l.address_line1, l.city, l.state, l.postal_code]
                .filter(Boolean)
                .join(", ")}
              {l.phone && <span className="ml-3">&middot; {l.phone}</span>}
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function LField({ label, testid, value, onChange, required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6358] mb-1">
        {label}
      </label>
      <input
        data-testid={testid}
        type="text"
        required={required}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
      />
    </div>
  );
}
